import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { PerformancePeriodDTO } from '../types';
import { dateToISO8601 } from '../helpers/dates';
import { QueryFeature } from '../../../config/queryClient';

interface SeriesDTO {
  color: string;
  name: string;
  percentage: number;
  value: number;
}

interface AllocationByAssetCategoryDTO {
  name: string;
  marketValue: number;
  series: SeriesDTO[];
}

interface FetcherParams {
  clientId: string;
  tab: 'left' | 'right';
  period?: PerformancePeriodDTO;
  startDate?: string;
  endDate?: string;
  accountId: string;
  groupId: string;
}

const fetchAllocationTab = async ({
  clientId,
  tab,
  period,
  startDate,
  endDate,
  accountId,
  groupId,
}: FetcherParams): Promise<AllocationByAssetCategoryDTO> =>
  authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/AllocationsTab',
      {
        clientId,
        tab,
        period,
        startDate,
        endDate,
        accountId: accountId || null,
        groupId: groupId || null,
        localDate: dateToISO8601(),
      },
      { skipNulls: true },
    ),
  );

export const ALLOCATION_TAB_QUERY_KEY = 'allocationTab';

interface QueryParams {
  clientId: string;
  tab: 'left' | 'right';
  period?: PerformancePeriodDTO;
  startDate: string;
  endDate: string;
  accountId: string;
  groupId: string;
}

export const useAllocationTabQuery = ({
  clientId,
  tab,
  period,
  startDate: _startDate,
  endDate: _endDate,
  accountId,
  groupId,
}: QueryParams) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  return useQuery(
    [
      QueryFeature.AccountPerformanceReporting,
      ALLOCATION_TAB_QUERY_KEY,
      clientId,
      tab,
      period,
      startDate,
      endDate,
      accountId,
      groupId,
    ],
    () =>
      fetchAllocationTab({
        clientId,
        tab,
        period,
        startDate,
        endDate,
        accountId,
        groupId,
      }),
    {
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
    },
  );
};
